import { useEffect, useRef, useState } from 'react';
import Header from '../Header/Header.jsx';
import Footer from '../Footer/Footer.jsx';
import styles from './CMPInvestorDeck.module.css';
import heroImg from '../../images/cmp-1.png';
import heroBgImg from '../../images/cmp-hero.png';
import Img2 from '../../images/cmp-2.png';
import Img3 from '../../images/cmp-3.png';
import Img4 from '../../images/cmp-4.png';
import Img5 from '../../images/cmp-5.png';

const slides = [
  {
    src: heroImg,
    title: 'Opening Cover',
    caption: 'Clear pre-seed positioning and investment snapshot.',
  },
  {
    src: Img2,
    title: 'The Problem',
    caption: 'Retail inventory pressure translated into a visual story.',
  },
  {
    src: Img3,
    title: 'Product Ladder',
    caption: 'Monitor, recovery and exchange presented as one system.',
  },
  {
    src: Img4,
    title: 'Technology Architecture',
    caption: 'A simplified input-to-recommendation product flow.',
  },
  {
    src: Img5,
    title: 'Closing Ask',
    caption: 'A high-contrast final slide with a direct investor message.',
  },
];

const projectMeta = [
  ['Project', 'Investor Deck'],
  ['Industry', 'Retail Technology'],
  ['Deliverable', '15-slide presentation'],
  ['Year', '2026'],
];

function useReveal() {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.dataset.visible = 'true';
          observer.unobserve(node);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -48px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return ref;
}

function Reveal({ children, className = '' }) {
  const ref = useReveal();

  return (
    <div ref={ref} className={`${styles.reveal} ${className}`}>
      {children}
    </div>
  );
}

export default function CMPInvestorDeck() {
  const [activeSlide, setActiveSlide] = useState(null);

  useEffect(() => {
    if (activeSlide === null) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setActiveSlide(null);
      if (event.key === 'ArrowRight') {
        setActiveSlide((current) => (current + 1) % slides.length);
      }
      if (event.key === 'ArrowLeft') {
        setActiveSlide((current) => (current - 1 + slides.length) % slides.length);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [activeSlide]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('graphic');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <main className={styles.page}>
      <section className={styles.hero}>
        <img src={heroBgImg} alt="CMP Investor Deck" className={styles.heroBgImage} />
        <div className={styles.heroOverlay} />
        <div className={styles.heroGlow} aria-hidden="true" />

        <div className={styles.container}>
          <div className={styles.heroGrid}>
            <Reveal className={styles.heroCopy}>
              <p className={styles.eyebrow}>Presentation Design · Case Study</p>
              <h1>Cash Margin Partners</h1>
              <p className={styles.lead}>
                A focused investor deck that turns a complex retail inventory problem
                into a clear product, technology and growth story.
              </p>

              <div className={styles.tags} aria-label="Project skills">
                <span>Investor Storytelling</span>
                <span>Information Design</span>
                <span>Visual Direction</span>
              </div>
            </Reveal>

            <Reveal className={styles.heroVisual}>
              <button
                type="button"
                className={styles.heroImageButton}
                onClick={() => setActiveSlide(0)}
                aria-label="Open cover slide preview"
              >
                <img
                  src={slides[0].src}
                  alt="Cash Margin Partners investor deck cover"
                  fetchPriority="high"
                />
                <span className={styles.viewBadge}>View slide ↗</span>
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      <section className={styles.overview}>
        <div className={styles.container}>
          <Reveal className={styles.overviewGrid}>
            <div>
              <p className={styles.sectionLabel}>Overview</p>
              <h2>Designed for clarity, confidence and fast decision-making.</h2>
            </div>

            <p className={styles.overviewText}>
              The visual system uses generous spacing, restrained colour, modular data
              cards and strong hierarchy to keep the investor narrative easy to scan.
            </p>
          </Reveal>

          <Reveal className={styles.metaGrid}>
            {projectMeta.map(([label, value]) => (
              <div className={styles.metaItem} key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className={styles.gallerySection}>
        <div className={styles.container}>
          <Reveal className={styles.sectionHeading}>
            <div>
              <p className={styles.sectionLabel}>Selected screens</p>
              <h2>A few key moments from the deck.</h2>
            </div>
            <p>Click any screen to view it larger.</p>
          </Reveal>

          <div className={styles.gallery}>
            {slides.slice(1).map((slide, index) => (
              <Reveal
                className={`${styles.slideCard} ${index === 0 || index === 3 ? styles.wideCard : ''}`}
                key={slide.title}
              >
                <button
                  type="button"
                  onClick={() => setActiveSlide(index + 1)}
                  aria-label={`Open ${slide.title}`}
                >
                  <span className={styles.imageFrame}>
                    <img src={slide.src} alt={slide.title} loading="lazy" />
                    <span className={styles.cardArrow} aria-hidden="true">↗</span>
                  </span>

                  <span className={styles.cardCopy}>
                    <span className={styles.cardNumber}>0{index + 2}</span>
                    <span>
                      <strong>{slide.title}</strong>
                      <small>{slide.caption}</small>
                    </span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.nextSection}>
        <div className={styles.container}>
          <Reveal className={styles.nextCard}>
            <div>
              <p className={styles.sectionLabel}>Explore more</p>
              <h2>See more selected design projects.</h2>
            </div>
            <a href="/#projects">View all projects <span aria-hidden="true">↗</span></a>
          </Reveal>
        </div>
      </section>

      <Footer mode={mode} background="black" />
      {activeSlide !== null && (
        <div
          className={styles.lightbox}
          role="dialog"
          aria-modal="true"
          aria-label={`${slides[activeSlide].title} preview`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setActiveSlide(null);
          }}
        >
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => setActiveSlide(null)}
            aria-label="Close preview"
          >
            ×
          </button>

          <button
            type="button"
            className={`${styles.navButton} ${styles.previousButton}`}
            onClick={() => setActiveSlide((activeSlide - 1 + slides.length) % slides.length)}
            aria-label="Previous slide"
          >
            ←
          </button>

          <figure className={styles.lightboxFigure}>
            <img src={slides[activeSlide].src} alt={slides[activeSlide].title} />
            <figcaption>
              <strong>{slides[activeSlide].title}</strong>
              <span>{activeSlide + 1} / {slides.length}</span>
            </figcaption>
          </figure>

          <button
            type="button"
            className={`${styles.navButton} ${styles.nextButton}`}
            onClick={() => setActiveSlide((activeSlide + 1) % slides.length)}
            aria-label="Next slide"
          >
            →
          </button>
        </div>
      )}
    </main>
    </>
  );
}